import {
  clampChatMode,
  DASH_CHAT_MODE,
  ARGON_CHAT_MODE,
  GOD_CHAT_MODE,
  MAIN_CHAT_MODE,
  RESEARCH_CHAT_MODE,
  ULTRA_CHAT_MODE,
} from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
} from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { RoundButton } from '@/design-system-v2/round-button';
import {
  researchQuotaLabel,
  useCurrentChatMode,
} from '@/features/chat/chat.hooks';
import { useShortcutLabel } from '@/features/shortcuts';
import { BuyUsageCreditsModal } from '@/features/subscription/buy-usage-credits-modal';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { useTabs } from '@/features/tabs';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { Routes } from '@/types/routes';
import { trackEvent } from '@/utils/analytics';
import { inElectron } from '@/utils/capabilities';

type ReasoningItemProps = {
  icon: ReactNode;
  label: string;
  description?: string;
  shortcut?: string;
  isActive: boolean;
  isLocked?: boolean;
  keepOpen?: boolean;
  compact?: boolean;
  testId: string;
  onSelect: () => void;
};

export const ReasoningItem = ({
  icon,
  label,
  description,
  shortcut,
  isActive,
  isLocked,
  keepOpen,
  compact,
  testId,
  onSelect,
}: ReasoningItemProps) => (
  <DropdownMenu.Item
    data-testid={testId}
    onSelect={(e) => {
      if (keepOpen) e.preventDefault();
      onSelect();
    }}
    className={cn(
      compact
        ? 'items-center gap-x-1.5 py-0.5'
        : 'grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-start gap-x-3 gap-y-0.5 py-2',
      isLocked && 'opacity-60'
    )}
  >
    <span
      className={cn(
        'shrink-0',
        compact ? '[&>svg]:size-2.5' : 'mt-0.5 [&>svg]:size-3.5'
      )}
    >
      {icon}
    </span>
    <span
      className={cn(
        'col-start-2 min-w-0 leading-none',
        compact ? 'text-[10px]' : 'text-sm leading-tight'
      )}
    >
      {label}
    </span>
    {!compact && description && (
      <span className="col-start-2 col-end-[-1] row-start-2 text-xs text-foreground-muted leading-tight">
        {description}
      </span>
    )}
    {shortcut && (
      <span
        className={cn(
          'col-start-3 row-start-1 ml-2 text-xs text-foreground-muted shrink-0',
          !compact && 'mt-0.5'
        )}
      >
        {shortcut}
      </span>
    )}
    {isActive && (
      <Icons.check
        className={cn(
          'col-start-4 row-start-1 shrink-0 text-foreground',
          compact ? 'size-2.5' : 'mt-[0.2rem] size-4'
        )}
      />
    )}
  </DropdownMenu.Item>
);

/**
 * Ultra is Pro-only; after a plan downgrade the persisted selection would keep
 * sending "ultra" (silently served as Max). Demote it to Max so the UI matches.
 * Waits for a real subscription snapshot so the pre-hydration BASIC default
 * cannot demote a legitimate Pro user's selection.
 */
export const useUltraChatModeGuard = () => {
  const { currentChatMode, setCurrentChatMode } = useCurrentChatMode();
  const isUltraMode = currentChatMode === ULTRA_CHAT_MODE;
  const isInternalUser = useIsInternalUser();
  const canUseUltra = subscriptionStore.isPro || isInternalUser;
  const subscriptionLoaded = subscriptionStore.state.lastUpdated !== null;

  useEffect(() => {
    if (isUltraMode && subscriptionLoaded && !canUseUltra) {
      setCurrentChatMode(RESEARCH_CHAT_MODE);
    }
  }, [isUltraMode, subscriptionLoaded, canUseUltra, setCurrentChatMode]);
};

export const useGodChatModeGuard = () => {
  const { currentChatMode, setCurrentChatMode } = useCurrentChatMode();
  const isGodMode = currentChatMode === GOD_CHAT_MODE;
  const canUseGod = subscriptionStore.isGodAvailable;
  const subscriptionLoaded = subscriptionStore.state.lastUpdated !== null;

  useEffect(() => {
    if (isGodMode && subscriptionLoaded && !canUseGod) {
      setCurrentChatMode(MAIN_CHAT_MODE);
    }
  }, [isGodMode, subscriptionLoaded, canUseGod, setCurrentChatMode]);
};

export const useArgonChatModeGuard = () => {
  const { currentChatMode, setCurrentChatMode } = useCurrentChatMode();
  const isArgonMode = currentChatMode === ARGON_CHAT_MODE;
  const canUseArgon = subscriptionStore.isArgonAvailable;
  const subscriptionLoaded = subscriptionStore.state.lastUpdated !== null;

  useEffect(() => {
    if (isArgonMode && subscriptionLoaded && !canUseArgon) {
      setCurrentChatMode(MAIN_CHAT_MODE);
    }
  }, [isArgonMode, subscriptionLoaded, canUseArgon, setCurrentChatMode]);
};

export const useTeamChatModeGuard = () => {
  const { currentChatMode, setCurrentChatMode } = useCurrentChatMode();
  const allowedChatMode =
    currentChatMode &&
    clampChatMode(currentChatMode, subscriptionStore.teamAllowedChatModes);

  useEffect(() => {
    if (allowedChatMode && allowedChatMode !== currentChatMode) {
      setCurrentChatMode(allowedChatMode);
    }
  }, [allowedChatMode, currentChatMode, setCurrentChatMode]);
};

export const IntegrationsMenuItems = () => {
  const { openTab } = useTabs();

  return (
    <>
      <DropdownMenu.Separator />
      <DropdownMenu.Item
        onSelect={() => openTab({ path: Routes.Integrations })}
      >
        <Icons.puzzle className="size-3.5 opacity-75" />
        Integrations
      </DropdownMenu.Item>
    </>
  );
};

export const ChatModeDropdown = observer(
  ({
    mode,
    setMode,
    onRequestUpgrade,
    modal,
    onOpenChange,
    contentProps,
    compact = false,
  }: {
    mode: string;
    setMode: (mode: string) => void;
    onRequestUpgrade: (source: string) => void;
    modal?: boolean;
    onOpenChange?: (open: boolean) => void;
    contentProps?: ComponentPropsWithoutRef<typeof DropdownMenu.Content>;
    compact?: boolean;
  }) => {
    const canUpgrade = subscriptionStore.canUpgrade;
    const isResearchLimitHit = subscriptionStore.isResearchLimitHit;
    const defaultShortcutLabel = useShortcutLabel('chat.modeDefault');
    const quickShortcutLabel = useShortcutLabel('chat.modeQuick');
    const maxShortcutLabel = useShortcutLabel('chat.modeMax');
    const ultraShortcutLabel = useShortcutLabel('chat.modeUltra');

    const isDashMode = mode === DASH_CHAT_MODE;
    const isUltraMode = mode === ULTRA_CHAT_MODE;
    const isGodMode = mode === GOD_CHAT_MODE;
    const isArgonMode = mode === ARGON_CHAT_MODE;
    const isDefaultResearchAgent = mode === RESEARCH_CHAT_MODE;
    const isNonDefaultResearchAgent =
      !isDefaultResearchAgent &&
      userStore.user?.chat_mode_details?.[mode]?.response_mode === 'research';
    const isResearchMode = isDefaultResearchAgent || isNonDefaultResearchAgent;

    const goToUpgrade = (source: string) => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source });
      onRequestUpgrade(source);
    };

    const limitReachedToast = (label: string, source: string) => {
      toast.info(`Daily ${label} limit reached`, {
        action: canUpgrade
          ? { label: 'Upgrade', onClick: () => goToUpgrade(source) }
          : undefined,
      });
    };

    const liteModeToast = () => {
      toast.info("You're on Lite, a smaller agent, until your usage resets", {
        action: {
          label: 'Add usage',
          onClick: () =>
            ModalsManager.showResolvable(BuyUsageCreditsModal, {
              onRequestUpgrade,
            }),
        },
      });
    };

    const isLiteModeActive = subscriptionStore.isLiteModeActive;

    const handleSelectDefault = () => {
      if (isLiteModeActive) {
        liteModeToast();
        return;
      }
      setMode(MAIN_CHAT_MODE);
    };

    const handleSelectQuick = () => {
      if (isDashMode) return;
      if (isLiteModeActive) {
        liteModeToast();
        return;
      }
      setMode(DASH_CHAT_MODE);
    };

    const handleSelectMax = () => {
      if (isLiteModeActive) {
        liteModeToast();
        return;
      }
      if (isResearchMode) return;
      if (isResearchLimitHit) {
        limitReachedToast(researchQuotaLabel(), 'research_limit_hit');
        return;
      }
      setMode(RESEARCH_CHAT_MODE);
    };

    const handleSelectUltra = () => {
      if (isLiteModeActive) {
        liteModeToast();
        return;
      }
      if (isUltraMode) return;
      // Ultra shares Max's daily quota
      if (isResearchLimitHit) {
        limitReachedToast(researchQuotaLabel(), 'ultra_limit_hit');
        return;
      }
      setMode(ULTRA_CHAT_MODE);
    };

    const handleSelectGod = () => {
      if (isLiteModeActive) {
        liteModeToast();
        return;
      }
      if (isGodMode) return;
      if (isResearchLimitHit) {
        limitReachedToast(researchQuotaLabel(), 'god_limit_hit');
        return;
      }
      setMode(GOD_CHAT_MODE);
    };

    const handleSelectArgon = () => {
      if (isLiteModeActive) {
        liteModeToast();
        return;
      }
      if (isArgonMode) return;
      setMode(ARGON_CHAT_MODE);
    };

    const limitReachedDescription = canUpgrade
      ? 'Daily limit reached — upgrade for more'
      : 'Daily limit reached';
    const defaultShortcut = inElectron() ? defaultShortcutLabel : undefined;
    const defaultDescription = 'For everyday tasks';
    const quickDescription = 'Fastest for quick answers';
    const maxDescription =
      !isResearchMode && isResearchLimitHit
        ? limitReachedDescription
        : 'For complex tasks';
    const ultraDescription =
      !isUltraMode && isResearchLimitHit
        ? limitReachedDescription
        : 'Our most capable mode';

    const triggerLabel = ((): string => {
      if (isLiteModeActive) return 'Lite';
      if (isUltraMode) return 'Ultra';
      if (isGodMode) return 'God';
      if (isArgonMode) return 'Argon';
      if (isResearchMode) {
        return isNonDefaultResearchAgent ? `Max · ${mode}` : 'Max';
      }
      if (isDashMode) return 'Quick';
      return 'Mode';
    })();

    // Ultra is Pro-only (the backend silently downgrades everyone else to Max)
    const isTeamAllowed = (chatMode: string) =>
      clampChatMode(chatMode, subscriptionStore.teamAllowedChatModes) ===
      chatMode;
    const showUltra =
      (subscriptionStore.isPro || userStore.isInternalUser) &&
      isTeamAllowed(ULTRA_CHAT_MODE);
    const showGod = subscriptionStore.isGodAvailable;
    const showArgon = subscriptionStore.isArgonAvailable;

    const activeChatMode = (():
      | 'lite'
      | 'default'
      | 'quick'
      | 'max'
      | 'ultra'
      | 'god'
      | 'argon' => {
      if (isLiteModeActive) return 'lite';
      if (isUltraMode) return 'ultra';
      if (isGodMode) return 'god';
      if (isArgonMode) return 'argon';
      if (isResearchMode) return 'max';
      if (isDashMode) return 'quick';
      return 'default';
    })();

    return (
      <DropdownMenu.Root modal={modal} onOpenChange={onOpenChange}>
        {compact ? (
          <DropdownMenu.Trigger
            variant="ghost"
            data-testid="chat-mode-dropdown-trigger"
            data-chat-mode={activeChatMode}
          >
            {triggerLabel}
            <Icons.chevronDown className="size-3 opacity-60" />
          </DropdownMenu.Trigger>
        ) : (
          <DropdownMenu.Trigger asChild>
            <RoundButton
              data-testid="chat-mode-dropdown-trigger"
              data-chat-mode={activeChatMode}
              className="h-8 border-transparent px-3"
            >
              {triggerLabel}
            </RoundButton>
          </DropdownMenu.Trigger>
        )}
        <DropdownMenu.Content
          {...contentProps}
          align="end"
          sideOffset={6}
          className={cn(compact ? 'w-44' : 'w-64')}
          data-testid="chat-mode-dropdown-content"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {isTeamAllowed(DASH_CHAT_MODE) && (
            <ReasoningItem
              testId="chat-mode-option-quick"
              icon={<Icons.fast />}
              label="Quick"
              description={compact ? undefined : quickDescription}
              shortcut={quickShortcutLabel}
              isActive={isDashMode}
              onSelect={handleSelectQuick}
            />
          )}
          <ReasoningItem
            testId="chat-mode-option-default"
            icon={<Icons.memory />}
            label="Default"
            description={compact ? undefined : defaultDescription}
            shortcut={defaultShortcut}
            isActive={
              !isDashMode &&
              !isResearchMode &&
              !isUltraMode &&
              !isGodMode &&
              !isArgonMode
            }
            onSelect={handleSelectDefault}
          />
          {isTeamAllowed(RESEARCH_CHAT_MODE) && (
            <ReasoningItem
              testId="chat-mode-option-max"
              icon={<Icons.research />}
              label={isNonDefaultResearchAgent ? `Max · ${mode}` : 'Max'}
              description={compact ? undefined : maxDescription}
              shortcut={maxShortcutLabel}
              isActive={isResearchMode}
              isLocked={!isResearchMode && isResearchLimitHit}
              keepOpen={!isResearchMode && isResearchLimitHit}
              onSelect={handleSelectMax}
            />
          )}
          {showUltra && (
            <ReasoningItem
              testId="chat-mode-option-ultra"
              icon={<Icons.sparkles />}
              label="Ultra"
              description={compact ? undefined : ultraDescription}
              shortcut={ultraShortcutLabel}
              isActive={isUltraMode}
              isLocked={!isUltraMode && isResearchLimitHit}
              keepOpen={!isUltraMode && isResearchLimitHit}
              onSelect={handleSelectUltra}
            />
          )}
          {showGod && (
            <ReasoningItem
              testId="chat-mode-option-god"
              icon={<Icons.crown />}
              label="God"
              description={
                compact ? undefined : 'Fable 5.1 research (internal)'
              }
              isActive={isGodMode}
              isLocked={!isGodMode && isResearchLimitHit}
              keepOpen={!isGodMode && isResearchLimitHit}
              onSelect={handleSelectGod}
            />
          )}
          {showArgon && (
            <ReasoningItem
              testId="chat-mode-option-argon"
              icon={<Icons.atom />}
              label="Argon"
              description={
                compact ? undefined : 'Gemini 4 Argon research (internal)'
              }
              isActive={isArgonMode}
              onSelect={handleSelectArgon}
            />
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);
